// /partner/developer — booking widget for your own website (every plan), API keys and webhooks (Scale / Scale+).
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, KeyRound, Trash2, Webhook, Globe, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const SITE = typeof window !== "undefined" ? window.location.origin : "https://fableplus.uk";
const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied")).catch(() => toast.error("Couldn't copy. Select the text and copy it instead."));
const Box = ({ title, icon: Icon, children, aside }) => (
  <section className="rounded-2xl border p-5">
    <div className="flex items-center justify-between gap-3"><h2 className="flex items-center gap-2 font-bold">{Icon && <Icon className="h-4 w-4" aria-hidden="true" />}{title}</h2>{aside}</div>
    <div className="mt-3">{children}</div>
  </section>
);
const Code = ({ children }) => (
  <div className="relative">
    <pre className="overflow-x-auto rounded-xl bg-muted p-3 pr-12 text-xs leading-relaxed"><code>{children}</code></pre>
    <button type="button" onClick={() => copy(children)} aria-label="Copy" className="absolute right-2 top-2 rounded-lg p-1.5 hover:bg-background"><Copy className="h-4 w-4" /></button>
  </div>
);

export default function PartnerDeveloper() {
  const [d, setD] = useState(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [shown, setShown] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api("/partner/developer").then(setD).catch((e) => toast.error(e.message)), []);
  useEffect(() => { document.title = "Website and API · Fable+"; load(); }, [load]);

  const run = async (key, fn) => { setBusy(key); try { await fn(); await load(); } catch (e) { toast.error(e.message); } finally { setBusy(""); } };
  const addKey = (e) => { e.preventDefault(); run("key", async () => { const r = await api("/partner/api-keys", { method: "POST", body: { name: name || "My key" } }); setShown({ label: "API key", value: r.key }); setName(""); }); };
  const addHook = (e) => { e.preventDefault(); run("hook", async () => { const r = await api("/partner/webhooks", { method: "POST", body: { url } }); setShown({ label: "Signing secret", value: r.secret }); setUrl(""); }); };
  const testHook = (id) => run("t" + id, async () => { const r = await api("/partner/webhooks/" + id + "/test", { method: "POST" }); r.ok ? toast.success("Delivered (" + r.status + ")") : toast.error("Your endpoint answered " + (r.status || "nothing") + ". Check the address and that it returns 2xx."); });

  if (!d) return <div className="h-64 animate-pulse rounded-2xl bg-muted" />;
  const pid = d.widget.provider_id;
  const snippet = '<script src="' + SITE + '/widget.js" data-business="' + pid + '" data-label="Book now" async></script>';

  return (
    <div className="space-y-6" data-testid="partner-developer">
      <div>
        <h1 className="text-2xl font-bold">Website and API</h1>
        <p className="mt-1 text-sm text-muted-foreground">Take bookings on your own website, and connect Fable+ to the tools you already use.</p>
      </div>

      {shown && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100" role="alert">
          <p className="font-semibold">{shown.label}: copy it now. We can't show it again.</p>
          <div className="mt-2"><Code>{shown.value}</Code></div>
          <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setShown(null)}>I've saved it</Button>
        </div>
      )}

      <Box title="Booking page and website button" icon={Globe}>
        <p className="text-sm text-muted-foreground">Your booking page works on any plan. Share the link, or paste the code before the closing body tag on your website to add a floating Book now button.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a href={SITE + "/book/" + pid} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary underline">{SITE.replace(/^https?:\/\//, "")}/book/{pid.slice(0, 8)}…</a>
          <Button type="button" variant="outline" size="sm" onClick={() => copy(SITE + "/book/" + pid)}>Copy link</Button>
        </div>
        <div className="mt-3"><Code>{snippet}</Code></div>
        <p className="mt-2 text-xs text-muted-foreground">Optional: data-color="#123456" to match your brand, data-position="left" to move it.</p>
      </Box>

      {!d.scale ? (
        <Box title="API and webhooks" icon={KeyRound}>
          <p className="text-sm text-muted-foreground">Scale and Scale+ plans can read bookings and listings through the Partner API and get a signed webhook the moment a booking is made, confirmed or cancelled. Use it with Zapier, Make, your CRM or your own systems.</p>
          <Button asChild className="mt-3"><Link to="/partner/plan">See Scale plans</Link></Button>
        </Box>
      ) : (
        <>
          <Box title="API keys" icon={KeyRound}>
            <form onSubmit={addKey} className="flex flex-wrap gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Name, e.g. Zapier" aria-label="Key name" autoComplete="off" data-1p-ignore className="h-10 min-w-0 flex-1 rounded-xl border bg-background px-3 text-sm" />
              <Button type="submit" disabled={busy === "key"}>Create key</Button>
            </form>
            <ul className="mt-3 divide-y rounded-xl border">
              {d.keys.map((k) => (
                <li key={k.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1"><span className="font-semibold">{k.name}</span> <span className="font-mono text-xs text-muted-foreground">fpk_…{k.last4}</span>
                    <span className="block text-xs text-muted-foreground">Created {String(k.created_at).slice(0, 10)}{k.last_used_at ? " · last used " + String(k.last_used_at).slice(0, 10) : " · not used yet"}</span></span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => run("r" + k.id, () => api("/partner/api-keys/" + k.id, { method: "DELETE" }))} aria-label={"Revoke " + k.name}><Trash2 className="h-4 w-4" /></Button>
                </li>
              ))}
              {!d.keys.length && <li className="px-3 py-3 text-sm text-muted-foreground">No keys yet.</li>}
            </ul>
            <div className="mt-4"><Code>{"curl -H \"X-Api-Key: YOUR_KEY\" " + SITE + "/api/partner-api/v1/bookings?limit=20"}</Code></div>
            <p className="mt-2 text-xs text-muted-foreground">Also /api/partner-api/v1/listings. Up to 120 requests a minute per key.</p>
          </Box>

          <Box title="Webhooks" icon={Webhook}>
            <form onSubmit={addHook} className="flex flex-wrap gap-2">
              <input value={url} onChange={(e) => setUrl(e.target.value)} type="url" required placeholder="https://your-site.com/fable-webhook" aria-label="Webhook address" autoComplete="off" className="h-10 min-w-0 flex-1 rounded-xl border bg-background px-3 text-sm" />
              <Button type="submit" disabled={busy === "hook"}>Add endpoint</Button>
            </form>
            <ul className="mt-3 divide-y rounded-xl border">
              {d.webhooks.map((h) => (
                <li key={h.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1"><span className="block truncate font-mono text-xs">{h.url}</span>
                    <span className="block text-xs text-muted-foreground">{h.active ? "Active" : "Paused" + (h.paused_reason ? ": " + h.paused_reason : "")}{h.last_status ? " · last reply " + h.last_status : ""}</span></span>
                  <Button type="button" variant="outline" size="sm" onClick={() => testHook(h.id)} disabled={busy === "t" + h.id}><Send className="mr-1 h-3.5 w-3.5" />Send test</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => run("d" + h.id, () => api("/partner/webhooks/" + h.id, { method: "DELETE" }))} aria-label="Delete endpoint"><Trash2 className="h-4 w-4" /></Button>
                </li>
              ))}
              {!d.webhooks.length && <li className="px-3 py-3 text-sm text-muted-foreground">No endpoints yet.</li>}
            </ul>
            <p className="mt-3 text-sm text-muted-foreground">Events: {d.events.join(", ")}. Each request has a Fable-Signature header: t=timestamp,v1=HMAC-SHA256 of "timestamp.body" with your signing secret.</p>
            <div className="mt-2"><Code>{"// Node: check a delivery\nconst [t, v1] = sig.split(',').map(p => p.split('=')[1]);\nconst ok = crypto.createHmac('sha256', SECRET).update(t + '.' + rawBody).digest('hex') === v1;"}</Code></div>
            {d.deliveries.length > 0 && (
              <details className="mt-3"><summary className="cursor-pointer text-sm font-semibold">Recent deliveries</summary>
                <ul className="mt-2 space-y-1 text-xs">{d.deliveries.map((x) => <li key={x.id} className="flex justify-between gap-2"><span>{x.event}</span><span className={x.ok ? "text-emerald-600" : "text-red-600"}>{x.status || "no reply"}</span><span className="text-muted-foreground">{String(x.at).slice(0, 16).replace("T", " ")}</span></li>)}</ul>
              </details>
            )}
          </Box>
        </>
      )}
    </div>
  );
}
